import { Directive, ElementRef, computed, inject, input, signal } from "@angular/core";
import { DevPanelFilter, normalizeForFilter } from "./dev-panel-filter";
import { DevPanelContext } from "./dev-panel-context";
import { DevAvailabilityRuleKey, resolveAvailability } from "./dev-availability";
import { DevRowType } from "./dev-row-type";

const FOCUSABLE_SELECTOR = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * Marks a template row as a filterable dev panel entry. The template is the
 * single source of truth for what a "dev panel item" is — there is no
 * parallel `DevItem[]` registry to keep in sync.
 */
@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: "[devRow]",
  standalone: true,
  host: {
    // A Tailwind `hidden` class would tie with the row's own `flex`/`grid`
    // display utility at equal specificity and lose depending on stylesheet
    // order; an inline style always wins. The `hidden` attribute is avoided
    // for the same reason: Tailwind v4 preflight does not guarantee it beats
    // a display utility.
    "[style.display]": "visible() ? null : 'none'",
  },
})
export class DevRowDirective {
  private readonly filter = inject(DevPanelFilter);
  private readonly elementRef: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly context = inject(DevPanelContext);
  private readonly footerExpanded = signal(false);

  readonly label = input.required<string>({ alias: "devRow" });
  readonly keywords = input<readonly string[]>([], { alias: "devRowKeywords" });
  readonly availabilityRule = input<DevAvailabilityRuleKey | undefined>(undefined, {
    alias: "devRowAvailability",
  });
  readonly routes = input<readonly string[]>([], { alias: "devRowRoutes" });
  readonly type = input<DevRowType>("display", { alias: "devRowType" });

  readonly haystack = computed(() => normalizeForFilter(`${this.label()} ${this.keywords().join(" ")}`));
  readonly matches = computed(() => {
    const normalizedQuery = this.filter.normalizedQuery();
    return normalizedQuery === "" || this.haystack().includes(normalizedQuery);
  });

  readonly availability = computed(() => resolveAvailability(this.availabilityRule(), this.context.availability()));

  /** True when this row is relevant to the page currently open. */
  readonly pinned = computed(() => this.routes().length > 0 && this.context.matchesRoute(this.routes()));

  /**
   * Unavailable rows drop into their section's footer — except pinned ones.
   * Route relevance outranks availability: a blocked-but-relevant control is
   * shown blocked rather than buried.
   */
  readonly sunk = computed(() => this.availability().tier !== "available" && !this.pinned());

  /**
   * True when the filter leaves this row on screen, ignoring whether the user
   * happens to have folded its section shut. Folding a group is a viewing
   * preference, not a filter result, so the panel's "N / M" counter is derived
   * from this rather than from `visible`.
   */
  readonly reachable = computed(() => this.matches() && (!this.sunk() || this.footerExpanded()));

  /**
   * True when this row should be visible on screen. A row is hidden when it
   * does not match the filter query or when it is sunk and its section's
   * footer is collapsed.
   */
  readonly visible = computed(() => this.reachable());

  /**
   * Called by DevSectionDirective via an effect to sync the footer state down
   * to sunk rows. This is a push rather than a pull because the pull approach
   * (injecting the parent section) creates a circular import.
   */
  setFooterExpanded(expanded: boolean): void {
    this.footerExpanded.set(expanded);
  }

  /**
   * The first focusable, non-disabled element inside the host, or the host
   * itself if it is focusable, else null.
   */
  focusTarget(): HTMLElement | null {
    const host = this.elementRef.nativeElement;
    const candidates = host.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
    const inner = Array.from(candidates).find((candidate) => !candidate.matches(":disabled"));
    if (inner) {
      return inner;
    }
    return this.isFocusable(host) ? host : null;
  }

  /**
   * Moves focus to this row without invoking it. Several rows are
   * destructive (Logout, Offline mode, Reload), so keyboard navigation to a
   * row must never fire it — only focus/scroll are performed here.
   */
  activate(): void {
    this.focusTarget()?.focus();
    this.elementRef.nativeElement.scrollIntoView({ block: "nearest" });
  }

  private isFocusable(element: HTMLElement): boolean {
    return element.matches(FOCUSABLE_SELECTOR) && !element.matches(":disabled");
  }
}
